import { useEffect, useState } from 'react'
import Lottie from 'lottie-react'
import type { WeatherCondition } from '@/hooks/useWeather'

// Animated Meteocons (fill style). We use the Lottie versions, not the SVGs: the SVGs
// animate via SMIL, which iOS WKWebView does NOT start for dynamically-inserted markup,
// so they render static on device. Lottie is JS-driven (requestAnimationFrame) and
// animates reliably everywhere. JSON is lazy-loaded per icon to keep the main bundle lean.
type MeteoSlug =
  | 'clear-day' | 'clear-night' | 'mostly-clear-day' | 'mostly-clear-night'
  | 'partly-cloudy-day' | 'partly-cloudy-night' | 'cloudy' | 'overcast'
  | 'sun-hot' | 'thermometer-colder' | 'fog' | 'haze' | 'smoke' | 'wind' | 'dust'
  | 'drizzle' | 'rain' | 'extreme-rain' | 'partly-cloudy-day-rain'
  | 'thunderstorms-day' | 'thunderstorms' | 'thunderstorms-rain'
  | 'hail' | 'sleet' | 'snow' | 'extreme-snow' | 'partly-cloudy-day-snow' | 'hurricane'

/** WeatherKit condition → Meteocons slug. */
const conditionSlug: Record<WeatherCondition, MeteoSlug> = {
  'clear-day': 'clear-day',
  'clear-night': 'clear-night',
  'mostly-clear-day': 'mostly-clear-day',
  'mostly-clear-night': 'mostly-clear-night',
  'partly-cloudy-day': 'partly-cloudy-day',
  'partly-cloudy-night': 'partly-cloudy-night',
  'mostly-cloudy': 'cloudy',
  cloudy: 'overcast',
  hot: 'sun-hot',
  frigid: 'thermometer-colder',
  fog: 'fog',
  haze: 'haze',
  smoke: 'smoke',
  breezy: 'wind',
  windy: 'wind',
  'blowing-dust': 'dust',
  drizzle: 'drizzle',
  rain: 'rain',
  'heavy-rain': 'extreme-rain',
  'sun-showers': 'partly-cloudy-day-rain',
  'isolated-thunderstorms': 'thunderstorms-day',
  'scattered-thunderstorms': 'thunderstorms',
  thunderstorm: 'thunderstorms-rain',
  'strong-storms': 'thunderstorms-rain',
  hail: 'hail',
  sleet: 'sleet',
  'freezing-drizzle': 'sleet',
  'freezing-rain': 'sleet',
  'wintry-mix': 'sleet',
  snow: 'snow',
  'heavy-snow': 'extreme-snow',
  flurries: 'snow',
  'sun-flurries': 'partly-cloudy-day-snow',
  blizzard: 'extreme-snow',
  'blowing-snow': 'snow',
  hurricane: 'hurricane',
  'tropical-storm': 'hurricane',
}

/** Lazy loaders per slug (static specifiers so Vite can code-split each JSON). */
const slugLoader: Record<MeteoSlug, () => Promise<{ default: object }>> = {
  'clear-day': () => import('@meteocons/lottie/fill/clear-day.json'),
  'clear-night': () => import('@meteocons/lottie/fill/clear-night.json'),
  'mostly-clear-day': () => import('@meteocons/lottie/fill/mostly-clear-day.json'),
  'mostly-clear-night': () => import('@meteocons/lottie/fill/mostly-clear-night.json'),
  'partly-cloudy-day': () => import('@meteocons/lottie/fill/partly-cloudy-day.json'),
  'partly-cloudy-night': () => import('@meteocons/lottie/fill/partly-cloudy-night.json'),
  cloudy: () => import('@meteocons/lottie/fill/cloudy.json'),
  overcast: () => import('@meteocons/lottie/fill/overcast.json'),
  'sun-hot': () => import('@meteocons/lottie/fill/sun-hot.json'),
  'thermometer-colder': () => import('@meteocons/lottie/fill/thermometer-colder.json'),
  fog: () => import('@meteocons/lottie/fill/fog.json'),
  haze: () => import('@meteocons/lottie/fill/haze.json'),
  smoke: () => import('@meteocons/lottie/fill/smoke.json'),
  wind: () => import('@meteocons/lottie/fill/wind.json'),
  dust: () => import('@meteocons/lottie/fill/dust.json'),
  drizzle: () => import('@meteocons/lottie/fill/drizzle.json'),
  rain: () => import('@meteocons/lottie/fill/rain.json'),
  'extreme-rain': () => import('@meteocons/lottie/fill/extreme-rain.json'),
  'partly-cloudy-day-rain': () => import('@meteocons/lottie/fill/partly-cloudy-day-rain.json'),
  'thunderstorms-day': () => import('@meteocons/lottie/fill/thunderstorms-day.json'),
  thunderstorms: () => import('@meteocons/lottie/fill/thunderstorms.json'),
  'thunderstorms-rain': () => import('@meteocons/lottie/fill/thunderstorms-rain.json'),
  hail: () => import('@meteocons/lottie/fill/hail.json'),
  sleet: () => import('@meteocons/lottie/fill/sleet.json'),
  snow: () => import('@meteocons/lottie/fill/snow.json'),
  'extreme-snow': () => import('@meteocons/lottie/fill/extreme-snow.json'),
  'partly-cloudy-day-snow': () => import('@meteocons/lottie/fill/partly-cloudy-day-snow.json'),
  hurricane: () => import('@meteocons/lottie/fill/hurricane.json'),
}

/**
 * Compact icons (sun/moon/thermometer) sit small in their frame; cloud-based icons fill
 * the frame fully and read ~15% larger at the same box size. Scale the cloud-family down
 * so every condition has consistent visual weight. Tune COMPACT_SCALE if needed.
 */
const COMPACT_SLUGS: ReadonlySet<MeteoSlug> = new Set([
  'clear-day',
  'clear-night',
  'mostly-clear-day',
  'mostly-clear-night',
  'sun-hot',
  'thermometer-colder',
])
const CLOUD_SCALE = 0.85

interface WeatherIconProps {
  condition: WeatherCondition
  /** Rendered px size (square). */
  size?: number
  className?: string
  /** Accessible label; falls back to the condition token. */
  label?: string
}

/** Animated Apple-style weather icon (Meteocons, Lottie) for a WeatherKit condition. */
export function WeatherIcon({ condition, size = 24, className, label }: WeatherIconProps) {
  const slug = conditionSlug[condition]
  const [data, setData] = useState<object | null>(null)

  useEffect(() => {
    let active = true
    slugLoader[slug]().then((mod) => {
      if (active) setData(mod.default)
    })
    return () => {
      active = false
    }
  }, [slug])

  return (
    <span
      role="img"
      aria-label={label ?? condition}
      style={{ width: size, height: size }}
      className={`inline-block shrink-0 ${className ?? ''}`}
    >
      {data && (
        <Lottie
          animationData={data}
          loop
          autoplay
          style={{
            width: '100%',
            height: '100%',
            transform: COMPACT_SLUGS.has(slug) ? undefined : `scale(${CLOUD_SCALE})`,
          }}
        />
      )}
    </span>
  )
}
